import { useEffect, useRef, useState } from 'react';
import { Loader2, Type, Phone, Info, Megaphone, Plus, Trash2, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useContentRefresh } from '../lib/content';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';

// Downscale a chosen photo in the browser so uploads are fast and always under the server limit
const fileToOptimisedDataUrl = (file, maxDim = 1920, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

const ImageUploadField = ({ label, value, slot, onUploaded, testid }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error('Please choose a JPG, PNG or WebP photo');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToOptimisedDataUrl(file);
      const res = await api.post('/admin/content/image', { image: dataUrl, slot });
      onUploaded(res.data.url);
      toast.success('Photo uploaded - click SAVE CHANGES to put it live');
    } catch {
      toast.error('Upload failed - please try a different photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <Label className="text-xs font-semibold text-[#53565A]">{label}</Label>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="h-20 w-32 rounded-lg border border-[#D9D9D6] bg-[#D9D9D6]/15 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img src={value} alt={`${label} preview`} className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-[#D9D9D6]" />
          )}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} />
          <Button
            type="button"
            variant="outline"
            data-testid={testid}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="rounded-full border-[#D9D9D6] h-10 px-5 text-xs font-bold tracking-wider"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {uploading ? 'UPLOADING...' : 'UPLOAD PHOTO'}
          </Button>
          <p className="text-[11px] text-[#53565A]/70 mt-1.5">JPG, PNG or WebP. It's resized automatically.</p>
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white border border-[#D9D9D6] rounded-xl p-6 space-y-4">
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#E4002B]" />
      </span>
      <div>
        <h2 className="font-heading text-xl tracking-wider text-[#24135F]">{title}</h2>
        {subtitle && <p className="text-xs text-[#53565A]/70">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold text-[#53565A]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls = 'h-11 rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]';

export default function SiteContentAdmin() {
  const refreshPublicContent = useContentRefresh();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/content')
      .then((res) => setContent(res.data))
      .catch(() => toast.error('Failed to load site content'));
  }, []);

  const setField = (section, key) => (e) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: e.target.value } }));

  const setListItem = (section, key, idx) => (e) =>
    setContent((c) => {
      const list = [...c[section][key]];
      list[idx] = e.target.value;
      return { ...c, [section]: { ...c[section], [key]: list } };
    });

  const setCard = (idx, key) => (e) =>
    setContent((c) => {
      const cards = c.why_choose.map((card, i) => (i === idx ? { ...card, [key]: e.target.value } : card));
      return { ...c, why_choose: cards };
    });

  const addListItem = (section, key) => () =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: [...c[section][key], ''] } }));

  const removeListItem = (section, key, idx) => () =>
    setContent((c) => ({
      ...c,
      [section]: { ...c[section], [key]: c[section][key].filter((_, i) => i !== idx) },
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = {
        ...content,
        hero: { ...content.hero, points: content.hero.points.filter((p) => p.trim()) },
        about: { ...content.about, bullets: content.about.bullets.filter((b) => b.trim()) },
      };
      await api.put('/admin/content', cleaned);
      refreshPublicContent();
      toast.success('Site content saved - changes are live');
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return (
      <div className="space-y-4 max-w-3xl">
        <Skeleton className="h-10 w-56" />
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">SITE CONTENT</h1>
          <p className="text-sm text-[#53565A]/70 mt-1">Edit the text and contact details shown on the public website.</p>
        </div>
      </div>

      <SectionCard icon={Type} title="HOMEPAGE HERO" subtitle="The big banner at the top of the homepage">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Headline line 1">
            <Input data-testid="content-hero-line1-input" className={inputCls} value={content.hero.headline_line1} onChange={setField('hero', 'headline_line1')} />
          </F>
          <F label="Headline line 2">
            <Input data-testid="content-hero-line2-input" className={inputCls} value={content.hero.headline_line2} onChange={setField('hero', 'headline_line2')} />
          </F>
        </div>
        <F label="Subheading">
          <Input data-testid="content-hero-subheading-input" className={inputCls} value={content.hero.subheading} onChange={setField('hero', 'subheading')} />
        </F>
        <F label="Checklist points">
          <div className="space-y-2">
            {content.hero.points.map((p, idx) => (
              <div key={idx} className="flex gap-2">
                <Input className={inputCls} value={p} onChange={setListItem('hero', 'points', idx)} />
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11 hover:bg-[#E4002B]/5 shrink-0" onClick={removeListItem('hero', 'points', idx)}>
                  <Trash2 className="h-4 w-4 text-[#E4002B]" />
                </Button>
              </div>
            ))}
            {content.hero.points.length < 6 && (
              <Button type="button" variant="outline" onClick={addListItem('hero', 'points')} className="rounded-full border-[#D9D9D6] h-9 px-4 text-xs font-bold tracking-wider">
                <Plus className="h-3.5 w-3.5 mr-1" /> ADD POINT
              </Button>
            )}
          </div>
        </F>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Primary button text">
            <Input className={inputCls} value={content.hero.primary_button} onChange={setField('hero', 'primary_button')} />
          </F>
          <F label="Secondary button text">
            <Input className={inputCls} value={content.hero.secondary_button} onChange={setField('hero', 'secondary_button')} />
          </F>
        </div>
        <ImageUploadField
          label="Hero photo"
          value={content.hero.image_url}
          slot="hero"
          testid="content-hero-image-upload"
          onUploaded={(url) => setContent((c) => ({ ...c, hero: { ...c.hero, image_url: url } }))}
        />
      </SectionCard>

      <SectionCard icon={Info} title="WHY CHOOSE CARDS" subtitle="The three cards under the hero">
        <div className="space-y-4">
          {content.why_choose.map((card, idx) => (
            <div key={idx} className="border border-[#D9D9D6]/50 rounded-lg p-4 space-y-3 bg-[#D9D9D6]/15/50">
              <F label={`Card ${idx + 1} title`}>
                <Input className={inputCls} value={card.title} onChange={setCard(idx, 'title')} />
              </F>
              <F label={`Card ${idx + 1} text`}>
                <Input className={inputCls} value={card.text} onChange={setCard(idx, 'text')} />
              </F>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={Phone} title="CONTACT DETAILS" subtitle="Shown on the contact page and in the footer">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Phone">
            <Input data-testid="content-contact-phone-input" className={inputCls} value={content.contact.phone} onChange={setField('contact', 'phone')} />
          </F>
          <F label="Email">
            <Input data-testid="content-contact-email-input" className={inputCls} value={content.contact.email} onChange={setField('contact', 'email')} />
          </F>
        </div>
        <F label="Address">
          <Input data-testid="content-contact-address-input" className={inputCls} value={content.contact.address} onChange={setField('contact', 'address')} />
        </F>
        <F label="Business hours (one per line)">
          <Textarea rows={3} className="rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]" value={content.contact.hours} onChange={setField('contact', 'hours')} />
        </F>
      </SectionCard>

      <SectionCard icon={Info} title="ABOUT PAGE">
        <F label="Paragraph 1">
          <Textarea rows={3} className="rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]" value={content.about.paragraph1} onChange={setField('about', 'paragraph1')} />
        </F>
        <F label="Paragraph 2">
          <Textarea rows={3} className="rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]" value={content.about.paragraph2} onChange={setField('about', 'paragraph2')} />
        </F>
        <F label="Checklist bullets">
          <div className="space-y-2">
            {content.about.bullets.map((b, idx) => (
              <div key={idx} className="flex gap-2">
                <Input className={inputCls} value={b} onChange={setListItem('about', 'bullets', idx)} />
                <Button type="button" variant="ghost" size="icon" className="h-11 w-11 hover:bg-[#E4002B]/5 shrink-0" onClick={removeListItem('about', 'bullets', idx)}>
                  <Trash2 className="h-4 w-4 text-[#E4002B]" />
                </Button>
              </div>
            ))}
            {content.about.bullets.length < 8 && (
              <Button type="button" variant="outline" onClick={addListItem('about', 'bullets')} className="rounded-full border-[#D9D9D6] h-9 px-4 text-xs font-bold tracking-wider">
                <Plus className="h-3.5 w-3.5 mr-1" /> ADD BULLET
              </Button>
            )}
          </div>
        </F>
        <ImageUploadField
          label="About page photo"
          value={content.about.image_url}
          slot="about"
          testid="content-about-image-upload"
          onUploaded={(url) => setContent((c) => ({ ...c, about: { ...c.about, image_url: url } }))}
        />
      </SectionCard>

      <SectionCard icon={Megaphone} title="BOTTOM CALL-TO-ACTION" subtitle="The black strip at the bottom of the homepage">
        <F label="Heading">
          <Input className={inputCls} value={content.cta.heading} onChange={setField('cta', 'heading')} />
        </F>
        <F label="Subtext">
          <Input className={inputCls} value={content.cta.subtext} onChange={setField('cta', 'subtext')} />
        </F>
        <F label="Button text">
          <Input className={inputCls} value={content.cta.button} onChange={setField('cta', 'button')} />
        </F>
        <F label="Footer blurb">
          <Textarea rows={3} className="rounded-lg focus-visible:ring-2 focus-visible:ring-[#E4002B]" value={content.footer.blurb} onChange={setField('footer', 'blurb')} />
        </F>
      </SectionCard>

      <div className="sticky bottom-4 z-20">
        <div className="bg-white/95 backdrop-blur border border-[#D9D9D6] rounded-full shadow-lg px-4 py-3 flex items-center justify-between gap-4">
          <p className="text-xs text-[#53565A] hidden sm:block">Changes go live on the public website as soon as you save.</p>
          <Button
            data-testid="content-save-button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-11 px-8 font-bold tracking-wider transition-colors duration-150 ml-auto"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </Button>
        </div>
      </div>
    </div>
  );
}
